import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Spinner, } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { noticeService, NoticeListItem } from "@/api/services/notice.service";
import moment from "moment";
import { dialogService } from "@/utils/dialogService";
const NoticeList = () => {
    const navigate = useNavigate();
    const [notices, setNotices] = useState<NoticeListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const fetchNotices = useCallback(async () => {
        try {
            setLoading(true);
            const response = await noticeService.getNotices();
            setNotices(response.data.results || []);
        }
        catch (error) {
            console.error("Failed to fetch notices:", error);
            toast.error("공지사항을 불러오는데 실패했습니다.");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchNotices();
    }, [fetchNotices]);
    const handleDelete = async (id: number) => {
        if (!await dialogService.confirm("이 공지사항을 삭제하시겠습니까?"))
            return;
        try {
            await noticeService.deleteNotice(id);
            toast.success("공지사항이 삭제되었습니다.");
            fetchNotices();
        }
        catch (error) {
            console.error("Failed to delete notice:", error);
            toast.error("삭제에 실패했습니다.");
        }
    };
    const formatDate = (dateStr: string | null) => {
        if (!dateStr)
            return "-";
        return moment(dateStr).format("YYYY-MM-DD");
    };
    const stripHtmlTags = (html: string | null | undefined) => {
        if (!html)
            return "";
        if (typeof window === "undefined") {
            return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
        }
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
    };
    return (<React.Fragment>
      <Toaster position="top-center"/>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="공지사항" pageTitle="공지사항 관리"/>
          <Card>
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <div className="col-sm">
                  <h5 className="card-title mb-0">공지사항 목록</h5>
                </div>
                <div className="col-sm-auto">
                  <Button color="primary" onClick={() => navigate("/notice-form/0")}>
                    <i className="ri-add-line align-bottom me-1"></i>
                    새 공지 작성
                  </Button>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              {loading ? (<div className="text-center py-4">
                  <Spinner color="primary"/>
                </div>) : notices.length === 0 ? (<div className="text-center py-4 text-muted">
                  등록된 공지사항이 없습니다.
                </div>) : (<div className="w-full overflow-x-auto">
                  <table className="w-full mb-0 text-sm border-y border-slate-200">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                      <tr>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "60px" }}>번호</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap">제목</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "80px" }}>이미지</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "80px" }}>상태</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "120px" }}>게시일</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "120px" }}>만료일</th>
                        <th className="h-12 px-4 text-left align-middle font-medium whitespace-nowrap" style={{ width: "120px" }}>관리</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {notices.map((notice, index) => {
                const isExpired = notice.expire_date && moment(notice.expire_date).isBefore(moment(), 'day');
                const previewText = stripHtmlTags(notice.preview);
                return (<tr key={notice.id} className={`${isExpired ? 'opacity-50' : 'hover:bg-slate-50/80'} transition-colors`}>
                            <td className="p-4 align-middle whitespace-nowrap text-slate-700">{notices.length - index}</td>
                            <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                              <div className="d-flex align-items-center gap-2">
                                {notice.is_highlight && (<span className="badge bg-warning-subtle text-warning">
                                    하이라이트
                                  </span>)}
                                <span className="text-primary font-medium" style={{ cursor: "pointer" }} onClick={() => navigate(`/notice-form/${notice.id}`)}>
                                  {notice.title}
                                </span>
                              </div>
                              {previewText && (<small className="text-muted d-block mt-1">
                                  {previewText}
                                </small>)}
                            </td>
                            <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                              {notice.first_image_url ? (<img src={notice.first_image_url} alt="" className="rounded" style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                        }}/>) : (<span className="text-muted">-</span>)}
                            </td>
                            <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                              {notice.is_published ? (<span className="badge bg-success">게시중</span>) : (<span className="badge bg-secondary">
                                  비공개
                                </span>)}
                            </td>
                            <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                              {formatDate(notice.display_date || notice.created_at)}
                            </td>
                            <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                              {notice.expire_date ? (<span className={isExpired ? 'text-danger' : 'text-slate-500'}>
                                  {formatDate(notice.expire_date)}
                                  {isExpired && ' (만료)'}
                                </span>) : (<span className="text-muted">-</span>)}
                            </td>
                            <td className="p-4 align-middle whitespace-nowrap text-slate-700">
                              <Button color="soft-primary" size="sm" className="me-1" onClick={() => navigate(`/notice-form/${notice.id}`)}>
                                수정
                              </Button>
                              <Button color="soft-danger" size="sm" onClick={() => handleDelete(notice.id)}>
                                삭제
                              </Button>
                            </td>
                          </tr>);
            })}
                    </tbody>
                  </table>
                </div>)}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>);
};
export default NoticeList;
